import React, { useState, useEffect } from 'react';
import { navigate, useLocation } from '@gatsbyjs/reach-router';
import JustificationContainer from '~/components/atom/JustificationContainer';
import {
  GetLfaConversionFilterType,
  type GetLfaConversionsTotalsQuery,
} from '~/graphql/types';
import LeadOverview from '../LeadOverview';
import CardsOverview from '../CardsOverview';
import EmptyStateComponent from '~/components/template/EmptyStateComponent';
import Loading from '~/components/atom/Loading';
import styled from 'styled-components';

const text = {
  emptyStateHeader: 'Selecteer een afspraak',
};

export type Props = {
  dataTestId?: string;
  className?: string;
  accountId: string;
  totals: GetLfaConversionsTotalsQuery;
  refetch: () => void;
};

// Helper function to convert URL param to filter type
const getFilterTypeFromUrl = (
  tab: string | null,
): GetLfaConversionFilterType => {
  if (!tab) return GetLfaConversionFilterType.Today;

  const upperTab = tab.toUpperCase();
  const validTabs = Object.values(GetLfaConversionFilterType);

  return (
    validTabs.find(filterType => filterType.toUpperCase() === upperTab) ||
    GetLfaConversionFilterType.Today
  );
};

// Helper function to convert filter type to URL param
const getUrlParamFromFilterType = (
  filterType: GetLfaConversionFilterType,
): string => filterType.toLowerCase();

const Content: React.FCC<Props> = ({
  dataTestId,
  className,
  accountId,
  totals,
  refetch,
}) => {
  const location = useLocation();

  // Extract tab parameter from URL search params
  const searchParams = new URLSearchParams(location.search);
  const tabFromUrl = searchParams.get('tab');

  const [selectedTab, setSelectedTab] = useState<GetLfaConversionFilterType>(
    getFilterTypeFromUrl(tabFromUrl),
  );
  const [selectedConversionId, setSelectedConversionId] = useState<
    string | null
  >(null);

  // Update selectedTab when URL changes
  useEffect(() => {
    const currentSearchParams = new URLSearchParams(location.search);
    const currentTab = currentSearchParams.get('tab');
    const filterType = getFilterTypeFromUrl(currentTab);
    setSelectedTab(filterType);
  }, [location.search]);

  // Handle tab change - update both state and URL
  const handleTabChange = (newTab: GetLfaConversionFilterType) => {
    setSelectedTab(newTab);
    // Create a new search params with only 'tab' set
    const searchParams = new URLSearchParams();
    searchParams.set('tab', getUrlParamFromFilterType(newTab));

    // Navigate to same path, but only with tab query param
    void navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: false,
    });
  };

  const hasConversionsInTab = totals[selectedTab.toLowerCase()].total > 0;

  return (
    <Container
      dataTestId={dataTestId}
      className={className}
      gap="l"
      width="100%"
    >
      <CardsOverview
        refetch={refetch}
        selectedTab={selectedTab}
        setSelectedTab={handleTabChange}
        totals={totals}
        selectedConversionId={selectedConversionId}
        setSelectedConversionId={setSelectedConversionId}
      />
      {selectedConversionId === null && !hasConversionsInTab && (
        <JustificationContainer
          align="center"
          justification="center"
          style={{ margin: '0 auto' }}
        >
          <EmptyStateComponent header={text.emptyStateHeader} />
        </JustificationContainer>
      )}
      {selectedConversionId === null && hasConversionsInTab && (
        <JustificationContainer
          align="center"
          justification="center"
          style={{ margin: '0 auto' }}
        >
          <Loading />
        </JustificationContainer>
      )}
      {selectedConversionId && (
        <JustificationContainer width="100%" style={{ position: 'relative' }}>
          <LeadOverview
            conversionId={selectedConversionId}
            accountId={accountId}
          />
        </JustificationContainer>
      )}
    </Container>
  );
};

const Container = styled(JustificationContainer)`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
`;

export default Content;
